import React from 'react';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import ChatIcon from '@mui/icons-material/Chat';
import { styled } from '@mui/material/styles';
import { Divider } from '@mui/material';

import { Avatar, theme, SingleLineText } from '@bloomays-lib/ui.shared';

export type buttonSize = 'large' | 'medium' | 'small';
export type AvatarUsernameProps = {
  name: string;
  jobTitle?: string;
  image?: string;
  size: buttonSize;
  notSeen?: boolean;
  topProfile?: boolean;
  becameBloomer?: boolean;
  caption?: string;
  commented?: boolean;
  onClick?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  activeHover?: boolean;
};

const ProfileCard = ({
  name,
  jobTitle,
  image,
  size,
  activeHover = false,
  onClick,
  notSeen,
  topProfile,
  becameBloomer,
  caption,
  commented,
}: AvatarUsernameProps) => {
  return (
    <ProfileContainer onClick={onClick} activeHover={activeHover} becameBloomer={becameBloomer}>
      <ProfileWrapper>
        <Profile>
          <AvatarInfoContainer>
            <Avatar image={image} size={size} alt={name} />
            <StyledDiv>
              <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                <ProfileName topProfile={topProfile} size={size} becameBloomer={becameBloomer}>
                  {name}
                </ProfileName>
                {notSeen && <NotSeenIcon />}
              </div>
              {jobTitle && (
                <ProfileJobTitle topProfile={topProfile} size={size} becameBloomer={becameBloomer}>
                  {jobTitle}
                </ProfileJobTitle>
              )}
            </StyledDiv>
          </AvatarInfoContainer>
          {topProfile && !becameBloomer && <IconTopProfile />}
          {becameBloomer && <IconBloomer />}
        </Profile>
        {(caption || commented) && (
          <>
            <Divider orientation="horizontal" flexItem />
            <CaptionContainer hasCaption={!!caption}>
              {caption && <SingleLineText text={caption} variant="caption" />}
              {commented && <IconChat />}
            </CaptionContainer>
          </>
        )}
      </ProfileWrapper>
    </ProfileContainer>
  );
};

export default ProfileCard;
export { ProfileCard };

const ProfileContainer = styled('div')<{
  onClick?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  activeHover?: boolean;
  becameBloomer?: boolean;
}>(({ theme, onClick, activeHover, becameBloomer }) => ({
  backgroundColor: becameBloomer ? theme.palette.green[100] : theme.palette.neutral.white,
  backgroundImage: becameBloomer
    ? `linear-gradient(0deg, ${theme.palette.red[80]} 0%, ${theme.palette.red[40]} 51%, ${theme.palette.beige[60]} 100%)`
    : theme.palette.neutral.white,
  padding: '10px',
  display: 'flex',
  gap: '10px',
  justifyContent: 'space-between',
  position: 'relative',
  alignItems: 'center',
  minHeight: '80px',
  borderRadius: becameBloomer ? '10px' : '0px',
  transition: 'box-shadow 0.3s ease-in-out',
  '&:hover': activeHover
    ? becameBloomer
      ? {
          cursor: onClick ? 'pointer' : 'auto',
          boxShadow: '0px 0px 12px rgba(0, 0, 0, 0.1)',
        }
      : {
          cursor: onClick ? 'pointer' : 'auto',
          backgroundColor: theme.palette.neutral[10],
          boxShadow: '0px 0px 12px rgba(0, 0, 0, 0.1)',
        }
    : undefined,
}));

const StyledDiv = styled('div')(() => ({
  textAlign: 'left',
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  gap: '5px',
}));

const IconTopProfile = styled(MilitaryTechIcon)(() => ({
  width: '32px',
  height: '32px',
  color: theme.palette.purple[100],
}));

const IconBloomer = styled(EmojiEventsIcon)(() => ({
  width: '32px',
  height: '32px',
  color: 'rgba(255, 255, 255, 0.82)',
}));

const IconChat = styled(ChatIcon)(() => ({
  width: '18px',
  height: '18px',
  color: theme.palette.neutral[30],
  marginRight: '6px',
}));

const NotSeenIcon = styled(FiberNewIcon)(() => ({
  width: '20px',
  height: '20px',
  color: theme.palette.red[60],
  top: '0',
}));

const ProfileWrapper = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  width: '100%',
}));

const Profile = styled('div')(() => ({
  display: 'flex',
  width: '100%',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '10px',
}));

const ProfileName = styled('div')<{ topProfile?: boolean; size: buttonSize; becameBloomer?: boolean }>(
  ({ topProfile, size, becameBloomer }) => ({
    fontFamily: 'Poppins Medium',
    fontSize: size === 'large' ? '18px' : '13px',
    lineHeight: '19.6px',
    color: becameBloomer
      ? theme.palette.neutral[80]
      : topProfile
      ? theme.palette.purple[100]
      : theme.palette.neutral[90],

    [theme.breakpoints.down('md')]: {
      fontSize: '14px',
      lineHeight: '12px',
    },
  }),
);

const ProfileJobTitle = styled('div')<{ topProfile?: boolean; size: buttonSize; becameBloomer?: boolean }>(
  ({ topProfile, size, becameBloomer }) => ({
    fontFamily: 'Poppins Light',
    fontSize: size === 'large' ? '12px' : '10px',
    lineHeight: size === 'large' ? '12px' : '11px',
    color: becameBloomer
      ? theme.palette.neutral[80]
      : topProfile
      ? theme.palette.purple[100]
      : theme.palette.neutral[70],
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    maxWidth: '100%',
    paddingBottom: '1px',

    [theme.breakpoints.down('lg')]: {
      fontSize: '12px',
      lineHeight: '13px',
    },

    [theme.breakpoints.down('md')]: {
      fontSize: '11px',
      lineHeight: '12px',
    },
  }),
);

const AvatarInfoContainer = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  flex: 1,
  minWidth: 0,
}));

const CaptionContainer = styled('div')<{ hasCaption: boolean }>(({ hasCaption }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  justifyContent: hasCaption ? 'space-between' : 'flex-end',
}));
